import {
  InvestmentAccountType,
  useGetEnrollmentAccountSelectionV2Data,
  useSubmitEnrollmentAccountSelectionV2Data,
} from '@egw/api'
import { ActionTile, Alert, AlertType, GenericError, Spinner } from '@egw/components'
import { WEALTH_ROUTES, type FeatureFlags } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { EnrollmentV2Layout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { CheckIcon } from '@heroicons/react/20/solid'
import { useMemo, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { PageHeader } from '../../../../components'
import { ROUTES } from '../../../../constants'

const log = new Logger('Enrollment - AccountSelectionV2')

export const AccountSelectionV2Page: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const {
    flags: { rolloutInheritedIraEnrollment, rolloutJointEnrollmentV1 },
  } = useFlags<FeatureFlags>()

  const [accountSelected, setAccountSelected] = useState<InvestmentAccountType | null>(InvestmentAccountType.ROTH_IRA)

  const { data, isLoading, error } = useGetEnrollmentAccountSelectionV2Data()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentAccountSelectionV2Data()

  const canEnrollRothIra =
    data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData?.userInfo?.investment?.canEnrollRothIra
  const canEnrollTraditionalIra =
    data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData?.userInfo?.investment?.canEnrollTraditionalIra
  const canEnrollBrokerage =
    data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData?.userInfo?.investment?.canEnrollBrokerage

  const canEnrollInheritedRothIra =
    data?.__typename === 'SubmitEnrollmentSuccess' &&
    data.enrollmentData?.userInfo?.investment?.canEnrollInheritedRothIra
  const canEnrollInheritedTraditionalIra =
    data?.__typename === 'SubmitEnrollmentSuccess' &&
    data.enrollmentData?.userInfo?.investment?.canEnrollInheritedTraditionalIra

  const onSubmit = async (accountType: InvestmentAccountType): Promise<void> => {
    setAccountSelected(accountType)
    try {
      const response = await mutate({
        investmentProfile: {
          account: {
            accountType,
          },
        },
      })
      if (response?.path) {
        navigate(response.path)
      } else {
        // TODO: Handle the case when path is not returned
        navigate(ROUTES.HOME.path)
      }
    } catch (e) {
      log.error('Error submitting email', e)
    } finally {
      setAccountSelected(null)
    }
  }

  const closeHandler = (): void => {
    try {
      void mutate({
        abandonApplication: true,
      })
      navigate(WEALTH_ROUTES.INVESTMENTS.path)
    } catch (e) {
      log.error('Error abandoning application', e)
    }
  }

  const title = useMemo(() => {
    return rolloutJointEnrollmentV1
      ? t('investments_v2.account_selection.title_joint_rollout')
      : t('investments_v2.account_selection.title')
  }, [rolloutJointEnrollmentV1, t])

  const description = useMemo(() => {
    return rolloutJointEnrollmentV1
      ? t('investments_v2.account_selection.description_joint_rollout')
      : t('investments_v2.account_selection.description')
  }, [rolloutJointEnrollmentV1, t])

  return (
    <EnrollmentV2Layout closeHandler={closeHandler}>
      <div className="h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <>
            <PageHeader title={t(title)} />

            <p className="typo-primary-sm-regular text-text-meta mb-xl">{t(description)}</p>

            <div className="flex flex-col gap-sm">
              {/* Brokerage & Cash Account */}
              {rolloutJointEnrollmentV1 ? (
                <ActionTile
                  title={t('investments_v2.account_selection.CASH_ACCOUNT.title_yeet_coastal')}
                  description={t('investments_v2.account_selection.CASH_ACCOUNT.description_yeet_coastal')}
                  onClick={() => void onSubmit(InvestmentAccountType.BROKERAGE)}
                  disabled={!canEnrollBrokerage || isSubmitting}
                >
                  {canEnrollBrokerage ? null : <DisabledChip />}
                  {accountSelected === InvestmentAccountType.BROKERAGE && isSubmitting ? (
                    <Spinner className="w-4 h-4 text-icons-muted" />
                  ) : null}
                </ActionTile>
              ) : null}
              <ActionTile
                title={t('investments_v2.account_selection.ROTH_IRA.title')}
                description={t('investments_v2.account_selection.ROTH_IRA.description')}
                onClick={() => void onSubmit(InvestmentAccountType.ROTH_IRA)}
                disabled={!canEnrollRothIra || isSubmitting}
              >
                {canEnrollRothIra ? null : <DisabledChip />}
                {accountSelected === InvestmentAccountType.ROTH_IRA && isSubmitting ? (
                  <Spinner className="w-4 h-4 text-icons-muted" />
                ) : null}
              </ActionTile>

              <ActionTile
                title={t('investments_v2.account_selection.TRADITIONAL_IRA.title')}
                description={t('investments_v2.account_selection.TRADITIONAL_IRA.description')}
                onClick={() => void onSubmit(InvestmentAccountType.TRADITIONAL_IRA)}
                disabled={!canEnrollTraditionalIra || isSubmitting}
              >
                {canEnrollTraditionalIra ? null : <DisabledChip />}
                {accountSelected === InvestmentAccountType.TRADITIONAL_IRA && isSubmitting ? (
                  <Spinner className="w-4 h-4 text-icons-muted" />
                ) : null}
              </ActionTile>

              {rolloutInheritedIraEnrollment ? (
                <>
                  <ActionTile
                    title={t('investments_v2.account_selection.TRADITIONAL_IRA_INHERITED.title')}
                    description={t('investments_v2.account_selection.TRADITIONAL_IRA_INHERITED.description')}
                    onClick={() => void onSubmit(InvestmentAccountType.TRADITIONAL_IRA_INHERITED)}
                    disabled={isSubmitting || !canEnrollInheritedTraditionalIra}
                  >
                    {canEnrollInheritedTraditionalIra ? null : <DisabledChip />}
                    {accountSelected === InvestmentAccountType.TRADITIONAL_IRA_INHERITED && isSubmitting ? (
                      <Spinner className="w-4 h-4 text-icons-muted" />
                    ) : null}
                  </ActionTile>

                  <ActionTile
                    title={t('investments_v2.account_selection.ROTH_IRA_INHERITED.title')}
                    description={t('investments_v2.account_selection.ROTH_IRA_INHERITED.description')}
                    onClick={() => void onSubmit(InvestmentAccountType.ROTH_IRA_INHERITED)}
                    disabled={isSubmitting || !canEnrollInheritedRothIra}
                  >
                    {canEnrollInheritedRothIra ? null : <DisabledChip />}
                    {accountSelected === InvestmentAccountType.ROTH_IRA_INHERITED && isSubmitting ? (
                      <Spinner className="w-4 h-4 text-icons-muted" />
                    ) : null}
                  </ActionTile>
                </>
              ) : null}
              {!rolloutJointEnrollmentV1 ? (
                <ActionTile
                  title={t('investments_v2.account_selection.BROKERAGE.title')}
                  description={t('investments_v2.account_selection.BROKERAGE.description')}
                  onClick={() => void onSubmit(InvestmentAccountType.BROKERAGE)}
                  disabled={!canEnrollBrokerage || isSubmitting}
                >
                  {canEnrollBrokerage ? null : <DisabledChip />}
                  {accountSelected === InvestmentAccountType.BROKERAGE && isSubmitting ? (
                    <Spinner className="w-4 h-4 text-icons-muted" />
                  ) : null}
                </ActionTile>
              ) : null}
              <ActionTile
                title={t('donor-advised.donor_advised_fund')}
                description={t('donor-advised.donor_advised_fund_description')}
                onClick={() => {
                  navigate(WEALTH_ROUTES.FOUNDATION_ACCOUNT.path)
                }}
              />
            </div>
            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}

      </div>
    </EnrollmentV2Layout>
  )
}

const DisabledChip: FC = () => {
  const { t } = useTranslation('features-enrollment')
  return (
    <div className="flex flex-row items-center gap-3xs rounded-full bg-gray-100 px-2xs py-3xs w-fit">
      <CheckIcon className="w-3 h-3 fill-icons-full" />
      <p className="typo-primary-2xs-semibold text-text-meta">{t('investments_v2.account_selection.already_opened')}</p>
    </div>
  )
}
